import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { CustomButtonGroup, StackedBarChart, HighchartBarChart } from '../../../shared';
import PerformanceSkeleton from '../monthlyPrescriptionPerformance/skeleton';
import { createChartData, sortDataWithMonthYear } from '../../../../utility';
import { actions as webMatrixActions } from '../../../../redux/reducers/webMatrixReducer';
const RESTRICTED_COMPANIES = [140,66,64,161];
const WEBSITE_OPTIONS = ['Sessions', 'Traffic by Channels', 'Traffic by Assets'];
const WEBSITE_DATA = {
    'Sessions': {
        xAxisCategories: [],
        dataSets: [],
        colors: []
    }, 
    'Traffic by Channels': {
        xAxisCategories: [],
        dataSets: [],
        colors: []
    },
    'Traffic by Assets': {
        xAxisCategories: [],
        dataSets: [],
        colors: []
    },
    'Quality Visits': {
        xAxisCategories: [],
        dataSets: [],
        colors: []
    }
};

const WebsitePerformance = (props) => {
    const dispatch = useDispatch();
    const { webMatrixData, isLoading } = useSelector((state) => state.webMatrix);
    const [data, setData] = useState(WEBSITE_DATA);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const { resetToInitialState } = webMatrixActions;
    const filteredWebsiteOptions = RESTRICTED_COMPANIES.includes(company?.id) 
        ? ['Sessions','Quality Visits'] 
        : WEBSITE_OPTIONS;
    const [selectedWebsiteOption, setSelectedWebsiteOption] = useState(filteredWebsiteOptions[0]);
    const handleWebsitePerformance = (option) => {      
        setSelectedWebsiteOption(option);
    };

    const handleChartOptions = (selectedOption, colors = [], xAxisCategories = []) => {
        let xAxisLabels = [];

        if(xAxisCategories && xAxisCategories.length > 0){
            xAxisLabels = sortDataWithMonthYear(xAxisCategories);
        }
        const options = {
            title: { text: null },
            subtitle: { text: null },
            chart: {
                backgroundColor: 'transparent',
                type: 'column'
            },
            colors: colors,
            mapNavigation: {
                enabled: false,
                enableButtons: false
            },
            navigation: { buttonOptions: { enabled: false } },
            credits: { enabled: false },
            tooltip: {
                enabled: true,
                headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
                pointFormat: '<span style="font-size: 12px">{series.name}: <b>' + ((selectedOption === 'Traffic by Channels' || selectedOption === 'Traffic by Assets') ? '{point.y}%' : '{point.y}') + '</b><br/></span>'

            },
            legend: { enabled: false },
            dataLabels: { enabled: false },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderRadius: 3,
                    maxPointWidth: 50
                }
            },
           
            xAxis: {
                categories: xAxisLabels,
                title: { text: 'Months' },
                lineColor: 'transparent'
                
            },
            yAxis: {
                min: 0,
                max: 100,
                title: { text: '' },
                stackLabels: { enabled: false },
                lineColor: 'transparent',
                gridLineWidth: 0
            }
        };

        if (selectedOption === 'Traffic by Channels' || selectedOption === 'Traffic by Assets') {
            return { ...options, yAxis: { ...options.yAxis, max: 100 } };
        } else {
            const { max, ...restYAxis } = options.yAxis;
            return { ...options, yAxis: { ...restYAxis, max: undefined } };
        }
        
    };

    const handleWebMatrixData = () => {
        if(webMatrixData && webMatrixData.sessions_chart_data && webMatrixData.sessions_chart_data.length > 0){
            const chartData = createChartData(webMatrixData.sessions_chart_data, 'sessions_value', true);
            setData((prevState) => ({
                ...prevState,
                Sessions: {
                    ...prevState.Sessions,
                    ...chartData
                }
            }));
        }
        if (selectedWebsiteOption === 'Quality Visits') {
            const chartData = createChartData(webMatrixData.quality_visits_chart_data, 'quality_visits_value', true);
            if (chartData?.xAxisCategories?.length > 0) {
                setData(prevState => ({
                    ...prevState,
                    'Quality Visits': {
                        xAxisCategories: chartData.xAxisCategories,
                        dataSets: chartData.dataSets,
                        colors: chartData.colors || ['#AD509E']
                    }
                }));
            }
        }

        if(webMatrixData && webMatrixData.source_medium_bar_chart_data && webMatrixData.source_medium_bar_chart_data.length > 0){
            const chartData = createChartData(webMatrixData.source_medium_bar_chart_data, 'source_medium_group_data');
            
            setData((prevState) => ({
                ...prevState,
                'Traffic by Channels': {
                    ...prevState['Traffic by Channels'],
                    ...chartData
                }
            }));
        }

        if(webMatrixData && webMatrixData.creative_name_bar_chart_data && webMatrixData.creative_name_bar_chart_data.length > 0){
            const chartData = createChartData(webMatrixData.creative_name_bar_chart_data , 'creative_name_data');

            setData((prevState) => ({
                ...prevState,
                'Traffic by Assets': {
                    ...prevState['Traffic by Assets'],
                    ...chartData
                }
            }));
        } else{
            setData(WEBSITE_DATA);
        }

    };

    useEffect(()=> {
        if(webMatrixData && Object.keys(webMatrixData).length > 0){
            handleWebMatrixData();
        }
    }, [webMatrixData,selectedWebsiteOption]);
    useEffect(()=>{
        dispatch(resetToInitialState());
    },[company,startDateRange,endDateRange]);
    return (
        <Box>
            <Typography className="text-lg-semi-bold gray-900">
                Website Performance
            </Typography>
            {isLoading ? (
                <PerformanceSkeleton />
            ) : (
                <>
                    <Box className="margin-tb-20">
                        <CustomButtonGroup
                            data={filteredWebsiteOptions}
                            handleClick={(option) => handleWebsitePerformance(option)}
                            key="monthly-prescription-performance"
                            selected={selectedWebsiteOption}
                        />
                    </Box>
                    <Box>
                        <HighchartBarChart
                            chartOptions={handleChartOptions(selectedWebsiteOption, 
                                data[selectedWebsiteOption].colors, data[selectedWebsiteOption].xAxisCategories)}
                            seriesData = {data[selectedWebsiteOption].dataSets}
                            seriesName={(selectedWebsiteOption === 'Traffic by Channels' || selectedWebsiteOption === 'Traffic by Assets') ? null : selectedWebsiteOption}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default WebsitePerformance;
